/**
 * TRUS-719 (Integrated Inbox, phase 4): voicemail playback, the typing
 * indicator (staff and member portal) and the org's ECM reminder switch.
 *
 * The backend ships in parallel, so every call here degrades quietly: a 404 /
 * 405 / 501 means "not deployed" and resolves to `null` (or a `missing` result)
 * so the caller hides the feature instead of showing an error.
 */
import { apiClient, handleApiError } from './apiClient';
import { memberApiFetch } from '@/lib/memberAuth';

// ---------------------------------------------------------------- helpers

export function errorStatus(error: unknown): number | undefined {
  const e = error as { response?: { status?: number }; status?: number };
  return e?.response?.status ?? (typeof e?.status === 'number' ? e.status : undefined);
}

/** 404 / 405 / 501: the endpoint (or feature) is not available on this backend. */
export function isUnavailableStatus(status: number | undefined): boolean {
  return status === 404 || status === 405 || status === 501;
}

// ---------------------------------------------------------------- typing

export type TypingKind = 'staff' | 'member';

export interface TypingEntry {
  kind: TypingKind;
  user_id: number | null;
  name: string;
}

/** `{typing: [...]}` (or a bare array) → clean entries; anything malformed is dropped. */
export function normalizeTyping(raw: unknown): TypingEntry[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { typing?: unknown }).typing)
      ? (raw as { typing: unknown[] }).typing
      : [];
  const out: TypingEntry[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const kind: TypingKind = r.kind === 'member' ? 'member' : 'staff';
    const id = typeof r.user_id === 'number' && Number.isFinite(r.user_id) ? r.user_id : null;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const k = `${kind}:${id ?? name}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ kind, user_id: id, name });
  }
  return out;
}

/** Staff side: `/api/messaging/channels/{id}/typing`. `null` = feature unavailable. */
export const staffTypingApi = {
  async get(channelId: number): Promise<TypingEntry[] | null> {
    try {
      const res = await apiClient.get(`/api/messaging/channels/${channelId}/typing`);
      return normalizeTyping(res.data);
    } catch (error) {
      if (isUnavailableStatus(errorStatus(error))) return null;
      throw handleApiError(error);
    }
  },
  /** Resolves false when the feature is unavailable. */
  async ping(channelId: number): Promise<boolean> {
    try {
      await apiClient.post(`/api/messaging/channels/${channelId}/typing`, {});
      return true;
    } catch (error) {
      if (isUnavailableStatus(errorStatus(error))) return false;
      throw handleApiError(error);
    }
  },
};

/** Member portal side: `/api/member-portal/inbox/threads/{key}/typing`. */
export const memberTypingApi = {
  async get(threadKey: string): Promise<TypingEntry[] | null> {
    const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/typing`);
    if (isUnavailableStatus(res.status)) return null;
    if (!res.ok) throw new Error(`typing ${res.status}`);
    // Members only ever see staff; drop anything else defensively.
    return normalizeTyping(await res.json().catch(() => null)).filter((e) => e.kind === 'staff');
  },
  async ping(threadKey: string): Promise<boolean> {
    const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/typing`, {
      method: 'POST',
      body: '{}',
    });
    if (isUnavailableStatus(res.status)) return false;
    if (!res.ok) throw new Error(`typing ${res.status}`);
    return true;
  },
};

// ---------------------------------------------------------------- voicemail

export type VoicemailAudioResult =
  | { kind: 'ok'; blob: Blob }
  /** 410 recording_unavailable: the provider no longer has the media. */
  | { kind: 'gone' }
  /** 404 / 501: no playback on this backend (or not visible). */
  | { kind: 'missing' };

/**
 * The voicemail recording, fetched with the staff session (bearer + X-CBO-ID)
 * so it can be played from an object URL. The provider URL is never exposed.
 */
export async function fetchVoicemailAudio(channelId: number, messageId: number): Promise<VoicemailAudioResult> {
  try {
    const res = await apiClient.get(`/api/messaging/channels/${channelId}/messages/${messageId}/voicemail-audio`, {
      responseType: 'blob',
    });
    const data = res.data as unknown;
    const type = String(res.headers?.['content-type'] ?? '') || 'audio/mpeg';
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type });
    return { kind: 'ok', blob };
  } catch (error) {
    const status = errorStatus(error);
    if (status === 410) return { kind: 'gone' };
    if (isUnavailableStatus(status)) return { kind: 'missing' };
    throw handleApiError(error);
  }
}

// ---------------------------------------------------------------- ECM reminders

export interface EcmReminderSettings {
  member_sms_email_enabled: boolean;
  can_edit: boolean;
}

export function normalizeEcmReminderSettings(raw: unknown): EcmReminderSettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    member_sms_email_enabled: r.member_sms_email_enabled === true,
    can_edit: r.can_edit === true,
  };
}

export const ecmReminderSettingsApi = {
  /** `null` when the endpoint is not deployed (the toggle is hidden). */
  async get(): Promise<EcmReminderSettings | null> {
    try {
      const res = await apiClient.get('/api/ecm/reminder-settings');
      return normalizeEcmReminderSettings(res.data);
    } catch (error) {
      if (isUnavailableStatus(errorStatus(error))) return null;
      throw handleApiError(error);
    }
  },
  async update(patch: Partial<Pick<EcmReminderSettings, 'member_sms_email_enabled'>>): Promise<EcmReminderSettings> {
    try {
      const res = await apiClient.patch('/api/ecm/reminder-settings', patch);
      return normalizeEcmReminderSettings(res.data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
